import {
  useId,
  useMemo,
  useState,
  type ChangeEvent,
  type ComponentProps,
} from "react";
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DesktopDatePicker,
  LocalizationProvider,
  MobileDatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { debug as debugFn } from "debug";
import { useField } from "formik";
import { useTranslation } from "react-i18next";

import { dayjs } from "@thalamos/common";
import type { Dayjs } from "@thalamos/common";
import type { TranslationKeyAndOptions } from "@thalamos/vision-common";

import { FormLabel } from "../FormLabel/FormLabel.js";

const debug = debugFn("vision-frontend:DatePicker");

export type DatePickerProps = {
  fieldName: string;
  label?: TranslationKeyAndOptions;
  disablePast?: boolean;
  disableFuture?: boolean;
  canBeUnknown?: boolean;
} & Pick<ComponentProps<typeof DesktopDatePicker>, "autoFocus">;

export type DatePickerOutput = {
  value: string | null;
  valueLabel?: string;
  isUnknown?: boolean;
};

export const DatePicker = ({
  fieldName,
  label,
  disablePast,
  disableFuture,
  canBeUnknown,
  autoFocus,
}: DatePickerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const ariaId = useId();

  const [field, meta, helpers] = useField<DatePickerOutput>(fieldName);

  // We use this to store the last value that the user typed in the text field.
  // This is so that we can restore it if they turn on the "unknown" flag.
  // We pre-populate this with the incoming "initialValue".
  const [lastTypedValue, setLastTypedValue] = useState<string | null>(
    (field.value as unknown as string) === "" ? null : field.value.value,
  );

  // Clean up the field value to be a structured object
  const fieldValue = useMemo(
    () =>
      (field.value as unknown as string) === ""
        ? // Turn empty string into a structured object
          { value: null, isUnknown: false }
        : field.value.isUnknown === true
          ? // If "unknown", clear out the value
            { value: null, isUnknown: true }
          : field.value,
    [field.value],
  );

  // The error could either come from Formik ("Invalid input") or from the schema
  // ({value: "string too short"}). We need to figure out which at runtime.
  const fieldError = useMemo(
    () => {
      const value =
        typeof meta.error === "string"
          ? meta.error
          : typeof meta.error === "object"
            ? (meta.error as { value: string } | undefined)?.value
            : undefined;

      return value === undefined ? undefined : t(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(meta.error)],
  );
  if (fieldError) {
    debug("fieldError", fieldError);
  }

  const handleDateChange = (value: Dayjs | null) => {
    const runAsync = async () => {
      // Parse the value
      const parsedValue =
        value && value.isValid() ? value.format("YYYY-MM-DD") : null;

      // Store away the thing they typed so we can restore it if they
      // turn on the unknown flag
      setLastTypedValue(parsedValue);

      const newValue = {
        value: parsedValue,
        ...(canBeUnknown ? { isUnknown: false } : {}),
      };

      debug("Setting value", newValue);
      await helpers.setValue(newValue);
      await helpers.setTouched(true);
    };

    void runAsync();
  };

  const handleUnknownChange = (event: ChangeEvent<HTMLInputElement>) => {
    const runAsync = async () => {
      const newValue = event.target.checked
        ? {
            value: null,
            isUnknown: true,
          }
        : {
            value: lastTypedValue,
            isUnknown: false,
          };

      debug("handleUnknownChange, Setting value", newValue);

      await helpers.setValue(newValue);
      await helpers.setTouched(true);
    };

    void runAsync();
  };

  const MUIDatePicker = isMobile ? MobileDatePicker : DesktopDatePicker;

  return (
    <Stack data-testid={`formField-${fieldName}`}>
      {label && <FormLabel {...label} htmlFor={ariaId} />}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box data-testid={`formField-${fieldName}-textField`}>
          <MUIDatePicker
            value={
              fieldValue.value || lastTypedValue
                ? dayjs(fieldValue.value || lastTypedValue, "YYYY-MM-DD")
                : null
            }
            onChange={handleDateChange}
            format="DD/MM/YYYY"
            disabled={fieldValue.isUnknown}
            disablePast={disablePast}
            disableFuture={disableFuture}
            autoFocus={autoFocus}
            slotProps={{
              textField: {
                error: meta.touched && Boolean(fieldError),
                // We always render the helper text so that the "unknown" checkbox
                // doesn't jump vertically when the error message appears.
                // Empty string would not be rendered, so we use a space.
                // We always render the helper text inside a consistent <span>
                // element so the DOM structure never changes when `meta.touched`
                // flips on blur. A non-breaking space (\u00A0) reserves the line
                // height so the layout doesn't shift when an error appears.
                helperText: (
                  <Box
                    component="span"
                    role={meta.touched && fieldError ? "alert" : undefined}
                  >
                    {meta.touched && fieldError ? fieldError : "\u00A0"}
                  </Box>
                ),
                fullWidth: true,
                inputProps: {
                  id: ariaId,
                  "data-testid": `formField-${fieldName}-textField-input`,
                },
              },
            }}
          />
        </Box>
      </LocalizationProvider>
      {canBeUnknown && (
        <FormControlLabel
          control={
            <Switch
              checked={fieldValue.isUnknown}
              onChange={handleUnknownChange}
              data-testid={`formField-${fieldName}-unknown`}
            />
          }
          label={t("common.unknown")}
        />
      )}
      {debug.enabled && (
        <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
          {JSON.stringify(field.value)}
        </Typography>
      )}
    </Stack>
  );
};
